import { FormControl, Radio, RadioGroup } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { useLocale } from 'shared-ui';

import { AppMessages } from 'i18n/messages';
import { UserPermissionsForm } from '../../UserPermissions.types';
import { EDIT_ONLY_FUNCTIONALITIES } from '../UserFunctionalPermissions';
import * as Styled from '../../UserPermissions.styles';

import { PermissionRowProps, PermissionOptions } from './PermissionRow.types';

export const PermissionRow = ({
  functionality,
  title,
  options = PermissionOptions.FULL,
  disabled = false,
  modifyDisabled = false,
}: PermissionRowProps) => {
  const { formatMessage } = useLocale();
  const { watch, setValue } = useFormContext<UserPermissionsForm>();
  const permissions = watch('permissions');

  const permission = permissions.find((p) => p.functionality === functionality);
  const permissionIndex = permissions.findIndex((p) => p.functionality === functionality);
  const isEditOnly = EDIT_ONLY_FUNCTIONALITIES.includes(functionality);

  if (!permission) return null;

  const canView = permission.viewAction;

  const handleAccessChange = (value: boolean) => {
    setValue(`permissions.${permissionIndex}.viewAction`, value, { shouldValidate: true, shouldDirty: true });

    if (isEditOnly) {
      setValue(`permissions.${permissionIndex}.modifyAction`, true, { shouldValidate: true, shouldDirty: true });
      return;
    }

    if (!value) {
      setValue(`permissions.${permissionIndex}.modifyAction`, false, { shouldValidate: true, shouldDirty: true });
    } else if (options === PermissionOptions.EDIT_ONLY) {
      setValue(`permissions.${permissionIndex}.modifyAction`, true, { shouldValidate: true, shouldDirty: true });
    }
  };

  const handleLevelChange = (value: boolean) => {
    if (isEditOnly) {
      setValue(`permissions.${permissionIndex}.modifyAction`, true, { shouldValidate: true, shouldDirty: true });
    } else {
      setValue(`permissions.${permissionIndex}.modifyAction`, value, { shouldValidate: true, shouldDirty: true });
    }
  };

  return (
    <Styled.PermissionRow key={functionality}>
      <Styled.PermissionTitle>{title}</Styled.PermissionTitle>

      <Styled.RadioGroupContainer>
        <FormControl component="fieldset" disabled={disabled}>
          <RadioGroup row value={canView} onChange={(e) => handleAccessChange(e.target.value === 'true')}>
            <Styled.FormControlLabel
              value="true"
              control={<Radio />}
              label={formatMessage({ id: AppMessages['common.yes'] })}
            />
            <Styled.FormControlLabel
              value="false"
              control={<Radio />}
              label={formatMessage({ id: AppMessages['common.no'] })}
            />
          </RadioGroup>
        </FormControl>
      </Styled.RadioGroupContainer>

      <Styled.RadioGroupContainer>
        <FormControl component="fieldset" disabled={disabled || !canView || modifyDisabled || isEditOnly}>
          <RadioGroup
            row
            value={isEditOnly || (canView ? permission.modifyAction : '')}
            onChange={(e) => handleLevelChange(e.target.value === 'true')}
          >
            {options === PermissionOptions.FULL ? (
              <Styled.FormControlLabel
                value="false"
                control={<Radio />}
                label={formatMessage({ id: AppMessages['permissions.action.read'] })}
              />
            ) : (
              <div></div>
            )}

            <Styled.FormControlLabel
              value="true"
              control={<Radio />}
              label={formatMessage({ id: AppMessages['permissions.action.edit'] })}
            />
          </RadioGroup>
        </FormControl>
      </Styled.RadioGroupContainer>
    </Styled.PermissionRow>
  );
};
